// Ball State University: public college page (lane 2, #2745), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Ball State's Common Data Set 2025-2026 (Office of Institutional
//    Research, Data and Strategy, file cds-2025-26.pdf): C1 applicants,
//    admits and enrollees for Fall 2025, C8 test policy for Fall 2027,
//    C21/C22 plans, G1/G5 2026-2027 costs. Quirk: the PDF's running page
//    header still reads "Common Data Set 2024-2025", but its C1 is Fall 2025
//    and its G1 is 2026-2027, so it is the 2025-2026 edition. Its C1
//    residency table reports every in-state applicant as admitted (13,125 of
//    13,125); that table is not shown here.
//  - Ball State Undergraduate Admissions: Dates and Deadlines (priority
//    deadlines; Ball State reviews on a rolling basis and publishes no
//    decision dates) and Admission Criteria for First-Time Freshmen (test
//    policy, the majors that need more than the application).
//  - College Scorecard API, school id 150136 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Ball State Common Data Set 2025-2026',
        url: 'https://www.bsu.edu/-/media/www/departmentalcontent/oirds/files/common-data-set/cds-2025-26.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Ball State Admissions, Dates and Deadlines',
        url: 'https://www.bsu.edu/admissions/undergraduate-admissions/apply-now/dates-and-deadlines',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Ball State Admissions, Admission Criteria for First-Time Freshmen',
        url: 'https://www.bsu.edu/admissions/undergraduate-admissions/apply-now/high-school-students',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?150136-Ball-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'ball-state-university',
    kbId: 'ball_state_university',
    name: 'Ball State University',
    shortName: 'Ball State',
    aliases: ['BSU'],
    location: 'Muncie, IN',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Ball State brand colors); decoration only.
    branding: {
        primary: '#BA0C2F',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/ball_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 22193,
        admitted: 19141,
        enrolled: 3720,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Ball State\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Ball State has no early decision or early action plan; the Common Data Set answers no to both. Its December 1 date is a priority deadline for scholarships, not an early round.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Ball State, and most majors need nothing more. Some ask for more than the application:',
        rows: [
            { school: 'School of Music; Theatre and Dance; Sports Production (Sports Link); Radiography; B/MD Pathway; Architecture, Interior Design, Urban Planning and Environmental Design', requirement: 'Additional steps beyond the university application, such as an audition, a portfolio or an essay, depending on the major.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'March 1' },
        rows: [
            { plan: 'Priority deadline, fullest scholarship consideration', deadline: 'December 1', decision: null, binding: false },
            { plan: 'Priority deadline', deadline: 'March 1', decision: null, binding: false },
        ],
        note: 'Ball State reviews applications on a rolling basis and decides once your materials are in; it publishes no decision dates. After a priority deadline, applications are considered as space allows, and everything must arrive at least two weeks before classes start. The Honors College priority deadline is November 15. The enrollment confirmation deadline is May 1.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Ball State is test-optional: you decide whether to send SAT, ACT or CLT scores. If you send them, it superscores, using your best scores.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted, for fall 2027 applicants, with scores due by August 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [8948, 27496] },
            { item: 'Required fees', amounts: [2134, 2134] },
            { item: 'Food and housing (on campus)', amounts: [12832, 12832] },
            { item: 'Books and supplies', amounts: [782, 782] },
            { item: 'Transportation', amounts: [436, 436] },
            { item: 'Other expenses', amounts: [2366, 2366] },
        ],
        totals: [27498, 46046],
        note: 'For a full-time undergraduate living on campus, fall and spring.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 13877,
            byIncome: [
                { income: '$0 to $30,000', amount: 8013 },
                { income: '$30,001 to $48,000', amount: 7780 },
                { income: '$48,001 to $75,000', amount: 10537 },
                { income: '$75,001 to $110,000', amount: 16425 },
                { income: 'Over $110,000', amount: 23618 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Ball State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
